// src/components/Finanzas/DetalleRelacion.jsx
// Detalle read-only de una RelacionGastos. Hidrata gastos + entregas + traslados
// y delega la presentacion al componente compartido RelacionDetalleContenido
// (mismo bloque que usan MiHistorialBolsillo y MisRelacionesGastos en su accordion).
//
// Muestra ademas la cabecera de la relacion y el listado de comprobantes
// contables generados (especifico de esta vista, no aparece en el accordion).

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Badge, Alert } from 'react-bootstrap';
import { FaFileInvoiceDollar, FaArrowLeft, FaFilePdf } from 'react-icons/fa';
import { Alertassw } from '../Alertas/Alertassw';
import {
  obtenerRelacion,
  listarComprobantes,
  listarGastos,
  listarEntregas,
  listarTraslados
} from '../../api/finanzas';
import RelacionDetalleContenido from './RelacionDetalleContenido';
import { descargarPdfRelacionLocal } from './utils/descargarPdfHelper';
import { formatoFechaColombia } from '../../utils/fechasColombia';

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const SYNC_BADGE = {
  PENDIENTE_SYNC: 'info',
  SINCRONIZANDO: 'info',
  SINCRONIZADO: 'success',
  SYNC_ERROR: 'danger',
  SYNC_NO_CONFIGURADO: 'warning',
  NO_REQUIERE_SYNC: 'secondary'
};

const ESTADO_BADGE = {
  BORRADOR: 'warning',
  APROBADA: 'info',
  SINCRONIZANDO: 'info',
  SINCRONIZADA: 'success',
  SYNC_ERROR: 'danger',
  CANCELADA: 'secondary'
};

const DetalleRelacion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [relacion, setRelacion] = useState(null);
  const [comprobantes, setComprobantes] = useState([]);
  const [gastos, setGastos] = useState([]);
  const [entregas, setEntregas] = useState([]);
  const [traslados, setTraslados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const cargar = async () => {
    setLoading(true);
    try {
      const rel = await obtenerRelacion(id);
      if (rel?.status !== 200) {
        throw new Error('No se pudo cargar la relación');
      }
      const r = rel.data.data;
      setRelacion(r);

      // Hidratacion en paralelo: comprobantes + gastos + entregas + traslados
      // por _id.$in (los endpoints existentes admiten el filtro).
      const gastosIds    = r.gastosIncluidos    || [];
      const entregasIds  = r.entregasIncluidas  || [];
      const trasladosIds = r.trasladosIncluidos || [];

      const [comps, gs, ents, trasls] = await Promise.all([
        listarComprobantes({ relacionGastos: r._id }).catch(() => ({ data: { data: [] } })),
        gastosIds.length
          ? listarGastos({ conditions: { _id: { $in: gastosIds } }, sort: { fecha: -1 } })
          : Promise.resolve({ data: { data: [] } }),
        entregasIds.length
          ? listarEntregas({ conditions: { _id: { $in: entregasIds } }, sort: { fecha: -1 } })
          : Promise.resolve({ data: { data: [] } }),
        trasladosIds.length
          ? listarTraslados({ conditions: { _id: { $in: trasladosIds } }, sort: { fecha: -1 } })
          : Promise.resolve({ data: { data: [] } })
      ]);

      setComprobantes(comps?.data?.data || []);
      setGastos(gs?.data?.data || []);
      setEntregas(ents?.data?.data || []);
      setTraslados(trasls?.data?.data || []);
    } catch (err) {
      console.error(err);
      Alertassw('error', 'Error', 'No se pudo cargar la relación');
    } finally {
      setLoading(false);
    }
  };

  const handleDescargarPdf = async () => {
    setDownloading(true);
    try {
      // El helper unificado hace la hidratacion completa y genera el PDF en cliente.
      await descargarPdfRelacionLocal({ relacion });
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
          <p className="mt-3">Cargando relación...</p>
        </div>
      </Container>
    );
  }

  if (!relacion) {
    return (
      <Container fluid className="py-4">
        <Alert variant="warning">Relación no encontrada</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 w-100">
      <Row className="mb-3 align-items-center">
        <Col xs="auto">
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-1" /> Volver
          </Button>
        </Col>
        <Col>
          <h2 className="text-primary fw-bold mb-0">
            <FaFileInvoiceDollar className="me-2" />
            {relacion.consecutivo}
          </h2>
          <p className="text-muted mb-0">Detalle de la relación de gastos</p>
        </Col>
        <Col xs="auto">
          <Button variant="danger" onClick={handleDescargarPdf} disabled={downloading}>
            <FaFilePdf className="me-1" /> {downloading ? 'Generando...' : 'Descargar PDF'}
          </Button>
        </Col>
      </Row>

      {/* Cabecera resumen */}
      <Card className="mb-4 shadow-sm border-start border-primary border-4 w-100">
        <Card.Body className="w-100">
          <Row>
            <Col md={6}>
              <p className="mb-1"><strong>Bolsillo:</strong> {relacion.bolsillo?.nombre || '—'}</p>
              <p className="mb-1"><strong>Estado:</strong>{' '}
                <Badge bg={ESTADO_BADGE[relacion.estado] || 'secondary'}>{relacion.estado}</Badge>
              </p>
              <p className="mb-1"><strong>Tipo:</strong> {relacion.tipo}</p>
              <p className="mb-0"><strong>Meses cubiertos:</strong>{' '}
                {(relacion.mesesRepresentados || []).join(', ') || '—'}
              </p>
            </Col>
            <Col md={6} className="text-md-end">
              <p className="mb-1">
                <strong>Fecha cierre:</strong>{' '}
                {relacion.fechaCierre ? formatoFechaColombia(relacion.fechaCierre) : '—'}
              </p>
              <p className="mb-1">
                <strong>Fecha aprobación:</strong>{' '}
                {relacion.fechaAprobacion ? formatoFechaColombia(relacion.fechaAprobacion) : '—'}
              </p>
              <p className="mb-0">
                <strong>Total gastos:</strong>{' '}
                <span className="fw-bold text-danger">{formatoCOP(relacion.montoTotalGastos)}</span>
              </p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Detalle compartido: saldos + gastos + entregas + traslados */}
      <Card className="mb-4 shadow-sm w-100">
        <Card.Body className="w-100">
          <RelacionDetalleContenido
            relacion={relacion}
            gastos={gastos}
            entregas={entregas}
            traslados={traslados}
            bolsilloId={relacion.bolsillo?._id || relacion.bolsillo}
          />
        </Card.Body>
      </Card>

      {/* Comprobantes contables generados (especifico de DetalleRelacion) */}
      {comprobantes.length > 0 && (
        <Card className="mb-4 shadow-sm w-100">
          <Card.Header className="bg-light w-100">
            <h5 className="mb-0">Comprobantes contables generados ({comprobantes.length})</h5>
          </Card.Header>
          <Card.Body className="w-100">
            <Table hover responsive size="sm">
              <thead className="table-light">
                <tr>
                  <th>Consecutivo</th>
                  <th>Mes</th>
                  <th>Tipo</th>
                  <th className="text-end">Total</th>
                  <th className="text-center">Sync</th>
                  <th>External ID</th>
                </tr>
              </thead>
              <tbody>
                {comprobantes.map((c) => (
                  <tr key={c._id}>
                    <td><strong>{c.consecutivo}</strong></td>
                    <td>{c.mes || '—'}</td>
                    <td><Badge bg="secondary">{(c.tipoComprobante || '').replace(/_/g, ' ')}</Badge></td>
                    <td className="text-end fw-bold">{formatoCOP(c.totalDebito)}</td>
                    <td className="text-center">
                      <Badge bg={SYNC_BADGE[c.syncStatus] || 'secondary'}>{c.syncStatus}</Badge>
                      {c.syncStatus === 'SYNC_ERROR' && (
                        <small className="d-block text-danger mt-1">{c.intentosSincronizacion} intentos</small>
                      )}
                    </td>
                    <td><code>{c.externalId || '—'}</code></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default DetalleRelacion;
